import type { Engineer } from '@lib/api-types/engineers';
import { useEffect, useState } from 'react';

import {
  useAssignEngineerTeam,
  useCreateTeam,
  useDeleteTeam,
  useRenameTeam,
  useSetTeamMembers,
  type Team,
} from '../api.ts';
import { Select } from '../components/Select.tsx';

/** The team an engineer is on, or null when unassigned (on no team). */
export function currentTeamId(engId: string, teams: Team[]): number | null {
  const t = teams.find((team) => team.member_ids.includes(engId));
  return t ? t.id : null;
}

/**
 * Teams management for Settings > Engineers. Lists teams (each expandable to
 * manage its members), an Assign modal for bulk add/remove, and an
 * Unassigned bucket. Every team — including the seeded "All Engineers" — can
 * be renamed and deleted; deleting a team leaves its members unassigned.
 */
export function TeamsManager({
  orgSlug,
  teams,
  engineers,
}: {
  orgSlug: string;
  teams: Team[];
  engineers: Engineer[];
}) {
  const create = useCreateTeam(orgSlug);
  const [name, setName] = useState('');
  const [expanded, setExpanded] = useState<number | 'unassigned' | null>(null);
  const [assignTeam, setAssignTeam] = useState<Team | null>(null);

  const assignedIds = new Set(teams.flatMap((t) => t.member_ids));
  const unassigned = engineers.filter((e) => !assignedIds.has(e.id));

  return (
    <section>
      <h2 className="settings-h2" style={{ marginBottom: '0.25rem' }}>
        Teams
      </h2>
      <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
        Group engineers into teams. Click a team to manage its members, or use Assign to add several
        at once. Engineers on no team are “Unassigned” (still counted under “All Teams”). Teams can
        be used to filter the Resilience, Timeline, and Talent views.
      </p>

      <ul style={listStyle}>
        {teams.map((t) => (
          <TeamRow
            key={t.id}
            orgSlug={orgSlug}
            team={t}
            teams={teams}
            engineers={engineers}
            expanded={expanded === t.id}
            onToggle={() => setExpanded((x) => (x === t.id ? null : t.id))}
            onAssign={() => setAssignTeam(t)}
          />
        ))}
        <UnassignedRow
          orgSlug={orgSlug}
          teams={teams}
          engineers={unassigned}
          expanded={expanded === 'unassigned'}
          onToggle={() => setExpanded((x) => (x === 'unassigned' ? null : 'unassigned'))}
        />
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const n = name.trim();
          if (!n) return;
          create.mutate(n, { onSuccess: () => setName('') });
        }}
        style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', maxWidth: '40rem' }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New team name"
          maxLength={80}
          style={{ ...inputStyle, flex: 1 }}
        />
        <button type="submit" disabled={create.isPending || !name.trim()}>
          {create.isPending ? 'Adding…' : 'Add team'}
        </button>
      </form>
      {create.isError ? <Err error={create.error} /> : null}

      {assignTeam ? (
        <AssignModal
          orgSlug={orgSlug}
          team={assignTeam}
          teams={teams}
          engineers={engineers}
          onClose={() => setAssignTeam(null)}
        />
      ) : null}
    </section>
  );
}

function TeamRow({
  orgSlug,
  team,
  teams,
  engineers,
  expanded,
  onToggle,
  onAssign,
}: {
  orgSlug: string;
  team: Team;
  teams: Team[];
  engineers: Engineer[];
  expanded: boolean;
  onToggle: () => void;
  onAssign: () => void;
}) {
  const rename = useRenameTeam(orgSlug);
  const del = useDeleteTeam(orgSlug);
  const assign = useAssignEngineerTeam(orgSlug);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(team.name);

  const members = engineers.filter((e) => team.member_ids.includes(e.id));

  return (
    <li style={cardStyle}>
      <div style={rowStyle}>
        {editing ? (
          <form
            style={{ display: 'flex', gap: '0.5rem', flex: 1 }}
            onSubmit={(e) => {
              e.preventDefault();
              const n = draft.trim();
              if (!n || n === team.name) return setEditing(false);
              rename.mutate({ teamId: team.id, name: n }, { onSuccess: () => setEditing(false) });
            }}
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              maxLength={80}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="submit" disabled={rename.isPending}>
              {rename.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setDraft(team.name); setEditing(false); }}>
              Cancel
            </button>
          </form>
        ) : (
          <>
            <button type="button" onClick={onToggle} style={discloseStyle} aria-expanded={expanded}>
              <span style={{ display: 'inline-block', width: '1rem' }}>{expanded ? '▾' : '▸'}</span>
              <strong>{team.name}</strong>
            </button>
            <span className="muted" style={{ fontSize: '0.9rem' }}>
              {members.length} {members.length === 1 ? 'engineer' : 'engineers'}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={() => { setDraft(team.name); setEditing(true); }}>
                Rename
              </button>
              <button type="button" onClick={onAssign}>
                Assign
              </button>
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => {
                  if (confirm(`Delete team “${team.name}”? Its engineers become unassigned.`)) {
                    del.mutate(team.id);
                  }
                }}
              >
                {del.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </>
        )}
      </div>

      {expanded && !editing ? (
        <div style={{ marginTop: '0.5rem', borderTop: '1px solid #eee', paddingTop: '0.5rem' }}>
          {members.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>No engineers on this team yet.</p>
          ) : (
            members.map((m) => (
              <MemberRow
                key={m.id}
                engineer={m}
                team={team}
                teams={teams}
                onMove={(teamId) => assign.mutate({ engineerId: m.id, teamId })}
                busy={assign.isPending}
              />
            ))
          )}
        </div>
      ) : null}
      {rename.isError ? <Err error={rename.error} /> : null}
      {del.isError ? <Err error={del.error} /> : null}
    </li>
  );
}

function MemberRow({
  engineer,
  team,
  teams,
  onMove,
  busy,
}: {
  engineer: Engineer;
  team: Team;
  teams: Team[];
  onMove: (teamId: number | null) => void;
  busy: boolean;
}) {
  const others = teams.filter((t) => t.id !== team.id);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.25rem 0' }}>
      <span>{engineer.display_name}</span>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {others.length > 0 ? (
          <Select
            value=""
            aria-label={`Move ${engineer.display_name} to another team`}
            onChange={(e) => {
              if (e.target.value) onMove(Number(e.target.value));
            }}
          >
            <option value="">Move to…</option>
            {others.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        ) : null}
        <button type="button" disabled={busy} onClick={() => onMove(null)}>
          Remove
        </button>
      </div>
    </div>
  );
}

function UnassignedRow({
  orgSlug,
  teams,
  engineers,
  expanded,
  onToggle,
}: {
  orgSlug: string;
  teams: Team[];
  engineers: Engineer[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const assign = useAssignEngineerTeam(orgSlug);
  return (
    <li style={cardStyle}>
      <div style={rowStyle}>
        <button type="button" onClick={onToggle} style={discloseStyle} aria-expanded={expanded}>
          <span style={{ display: 'inline-block', width: '1rem' }}>{expanded ? '▾' : '▸'}</span>
          <strong>Unassigned</strong>
        </button>
        <span className="muted" style={{ fontSize: '0.9rem' }}>
          {engineers.length} {engineers.length === 1 ? 'engineer' : 'engineers'}
        </span>
        <span />
      </div>
      {expanded ? (
        <div style={{ marginTop: '0.5rem', borderTop: '1px solid #eee', paddingTop: '0.5rem' }}>
          {engineers.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Everyone is on a team.</p>
          ) : teams.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Create a team to assign these engineers.</p>
          ) : (
            engineers.map((m) => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                <span>{m.display_name}</span>
                <Select
                  value=""
                  aria-label={`Assign ${m.display_name} to a team`}
                  onChange={(e) => {
                    if (e.target.value) assign.mutate({ engineerId: m.id, teamId: Number(e.target.value) });
                  }}
                >
                  <option value="">Assign to…</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </div>
            ))
          )}
        </div>
      ) : null}
    </li>
  );
}

function AssignModal({
  orgSlug,
  team,
  teams,
  engineers,
  onClose,
}: {
  orgSlug: string;
  team: Team;
  teams: Team[];
  engineers: Engineer[];
  onClose: () => void;
}) {
  const setMembers = useSetTeamMembers(orgSlug);
  const initial = new Set(team.member_ids);
  const [selected, setSelected] = useState<Set<string>>(new Set(initial));

  const dirty = initial.size !== selected.size || [...selected].some((id) => !initial.has(id));

  const tryClose = () => {
    if (dirty && !confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') tryClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const save = () =>
    setMembers.mutate({ teamId: team.id, engineerIds: [...selected] }, { onSuccess: onClose });

  const teamNameOf = (engId: string): string | null => {
    const t = teams.find((tm) => tm.id !== team.id && tm.member_ids.includes(engId));
    return t ? t.name : null;
  };

  return (
    <div style={overlayStyle} onMouseDown={tryClose}>
      <div
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-label={`Assign engineers to ${team.name}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: 0, fontFamily: 'var(--display)', fontWeight: 500, fontSize: '20px' }}>
          Assign to {team.name}
        </h2>
        <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
          Tick the engineers on this team. Ticking someone currently on another team moves them here.
        </p>
        <div style={{ overflowY: 'auto', maxHeight: '50vh', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          {engineers.map((e) => {
            const other = teamNameOf(e.id);
            return (
              <label key={e.id} style={checkRowStyle}>
                <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
                <span style={{ flex: 1 }}>{e.display_name}</span>
                {other ? <span className="muted" style={{ fontSize: '0.8rem' }}>on {other}</span> : null}
              </label>
            );
          })}
          {engineers.length === 0 ? <p className="muted">No engineers yet.</p> : null}
        </div>
        {setMembers.isError ? <Err error={setMembers.error} /> : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" onClick={tryClose} disabled={setMembers.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={save}
            disabled={!dirty || setMembers.isPending}
          >
            {setMembers.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Err({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div role="alert" style={{ color: '#c33', marginTop: '0.5rem', fontSize: '0.9rem' }}>
      {message}
    </div>
  );
}

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--rule, #ddd)',
  borderRadius: '6px',
  padding: '0.6rem 0.75rem',
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
};
const discloseStyle: React.CSSProperties = {
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '15px',
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  flex: 1,
  textAlign: 'left',
};
const inputStyle: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  border: '1px solid var(--rule, #ddd)',
  borderRadius: '4px',
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};
const modalStyle: React.CSSProperties = {
  background: 'var(--card, #fff)',
  borderRadius: '10px',
  width: 'min(460px, 94vw)',
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
const checkRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.3rem 0.25rem',
  cursor: 'pointer',
};
